<template>
    <div id="files-index-page" class="dashboard-view">
        <div class="container">
            <section class="content">
                <h1>Files</h1>

                <div class="table-header" style="margin-bottom:20px;">
                    <div class="table-header__tabs">
                        <button :class="['tab-btn', currentTab === 'log-files' && 'active']" @click="switchTab('log-files')">Log Files</button>
                        <button :class="['tab-btn', currentTab === 'leak-files' && 'active']" @click="switchTab('leak-files')">Leaked Files</button>
                    </div>
                </div>

                <DataTable v-if="currentTab === 'log-files'"
                           class="files-data-table"
                           :dataUrl="logFilesUrl"
                           :displayId="false"
                           :key="'log-' + logTableRefreshKey"
                           :dataFormatter="formatLogData"
                           :sortable="true"
                           @actionTriggered="actionTriggered">
                    <template v-slot:empty>No records found</template>
                </DataTable>

                <DataTable v-if="currentTab === 'leak-files'"
                           class="files-data-table"
                           :dataUrl="leakFilesUrl"
                           :displayId="false"
                           :key="'leak-' + leakTableRefreshKey"
                           :dataFormatter="formatLeakData"
                           :sortable="true"
                           @actionTriggered="actionTriggered">
                    <template v-slot:empty>No records found</template>
                </DataTable>
            </section>
        </div>
    </div>
</template>

<script>
import DataTable from '@/components/global/DataTable.vue'
import axios from 'axios'
import { mapStores } from 'pinia'
import { useAuthStore } from '@/stores/auth.js'

const apiBase = process.env.VUE_APP_API_BASE

export default {
    name: 'FilesIndex',
    components: { DataTable },
    computed: {
        ...mapStores(useAuthStore)
    },
    data() {
        return {
            currentTab: 'log-files',
            logFilesUrl: apiBase + '/log-files',
            leakFilesUrl: apiBase + '/leak-files',
            logTableRefreshKey: null,
            leakTableRefreshKey: null
        }
    },
    methods: {
        switchTab(tab) {
            this.currentTab = tab
        },
        formatSize(bytes) {
            if (bytes == null) return '-'
            if (bytes < 1024) return bytes + ' B'
            if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
            return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
        },
        formatLogData(data) {
            const newData = []
            for (const obj of data) {
                const newObj = {}

                newObj['_id'] = { label: '_id', value: obj.filename, display_type: 'text' }
                newObj['filename'] = { label: 'Filename', value: obj.filename, display_type: 'text' }

                if (obj.device) {
                    newObj['device'] = {
                        label: 'Device',
                        value: obj.device,
                        display_type: 'object',
                        hyper_label: obj.device.nickname || 'Device Info'
                    }
                    newObj['user'] = {
                        label: 'User',
                        value: obj.device.user?.name || '-',
                        display_type: 'text'
                    }
                } else {
                    newObj['device'] = { label: 'Device', value: '-', display_type: 'text' }
                    newObj['user'] = { label: 'User', value: '-', display_type: 'text' }
                }

                newObj['size'] = { label: 'Size', value: this.formatSize(obj.size), display_type: 'text' }
                newObj['uploadedAt'] = { label: 'uploadedAt', value: obj.uploadedAt, display_type: 'date' }
                newObj['actions'] = {
                    label: '',
                    value: {
                        download: `<i class="file-download-action" title="Download">Download</i>`,
                        delete: `<i class="file-delete-action" title="Delete">Delete</i>`
                    },
                    display_type: 'actions'
                }

                newData.push(newObj)
            }
            return newData
        },
        formatLeakData(data) {
            const newData = []
            for (const obj of data) {
                const newObj = {}

                newObj['_id'] = { label: '_id', value: obj.filename, display_type: 'text' }
                newObj['filename'] = { label: 'Filename', value: obj.filename, display_type: 'text' }

                if (obj.user) {
                    newObj['user'] = {
                        label: 'User',
                        value: obj.user,
                        display_type: 'object',
                        hyper_label: obj.user.name || 'User Info'
                    }
                } else {
                    newObj['user'] = { label: 'User', value: '-', display_type: 'text' }
                }

                newObj['size'] = { label: 'Size', value: this.formatSize(obj.size), display_type: 'text' }
                newObj['uploadedAt'] = { label: 'uploadedAt', value: obj.uploadedAt, display_type: 'date' }
                newObj['actions'] = {
                    label: '',
                    value: {
                        download: `<i class="file-download-action" title="Download">Download</i>`,
                        delete: `<i class="file-delete-action" title="Delete">Delete</i>`
                    },
                    display_type: 'actions'
                }

                newData.push(newObj)
            }
            return newData
        },
        actionTriggered(actionKey, filename) {
            if (!filename) {
                alert('Error occurred #89')
                return
            }
            const fileType = this.currentTab === 'log-files' ? 'log-files' : 'leak-files'
            if (actionKey === 'download') {
                const url = `${apiBase}/${fileType}/${encodeURIComponent(filename)}/download?token=${this.authStore.token}`
                window.open(url, '_blank')
            }
            else if (actionKey === 'delete') {
                if (confirm('Are you sure you want to delete this file?')) {
                    axios.delete(`${apiBase}/${fileType}/${encodeURIComponent(filename)}`)
                        .then(response => {
                            if (response.data.success === true) {
                                if (this.currentTab === 'log-files') {
                                    this.logTableRefreshKey = Date.now()
                                } else {
                                    this.leakTableRefreshKey = Date.now()
                                }
                            } else {
                                alert('Error occurred')
                                console.log(response.data.data)
                            }
                        })
                        .catch(error => {
                            alert('Error occurred while deleting')
                            console.log(error)
                        })
                }
            }
        }
    }
}
</script>

<style lang="scss" scoped>
.table-header__tabs {
    display: flex;
    gap: 4px;
}

.tab-btn {
    background: none;
    border: none;
    border-bottom: 2px solid transparent;
    padding: 6px 16px;
    cursor: pointer;
    font-size: $font-size;
    font-family: $font-family;
    color: $secondary-color;

    &.active {
        color: $text-color;
        border-bottom-color: $brand-color;
        font-weight: $font-weight-bold;
    }
}
</style>
